import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services';
import { getLanguage } from '../utils/func';
import { feedViewerContext } from '../utils/searchFeedReturn';

// Omitting area keeps the embedded/legacy API contract unchanged. Event pages
// opt into server-side groups and search; no country or followed IDs come from
// local storage. Keep title loading independent of every filter/keystroke.
export default function useStoryDetails(storyId, kind, {
  loadTitle = true, initialStory = null, area, query = '', composing = false, viewerKey = '',
} = {}) {
  const language = getLanguage();
  const initialLanguage = useRef(language);
  const viewer = feedViewerContext();
  const scopeKey = JSON.stringify([storyId, kind, viewerKey, viewer]);
  const titleKey = JSON.stringify([storyId, language]);
  const initialTitle = initialLanguage.current === language && Number(initialStory?.id) === storyId && typeof initialStory?.title === 'string'
    ? initialStory.title : '';
  const knownStory = initialTitle ? { id: storyId, title: initialTitle } : {};
  const text = kind === 'opinions' ? query.normalize('NFKC').trim().replace(/\s+/g, ' ') : '';
  const key = JSON.stringify([scopeKey, area, text, composing]);
  const validId = Number.isSafeInteger(storyId) && storyId > 0;
  const [attempt, setAttempt] = useState(0);
  const [title, setTitle] = useState({ key: titleKey, story: knownStory, status: loadTitle ? 'loading' : 'ready' });
  const [data, setData] = useState({ key, status: 'loading', list: [], emptyReason: null });
  const [accepted, setAccepted] = useState(null);
  const retry = useCallback(() => setAttempt(value => value + 1), []);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setTitle(previous => ({ key: titleKey, status: loadTitle ? 'loading' : 'ready',
      story: previous.key === titleKey && previous.story.title ? previous.story
        : initialTitle ? { id: storyId, title: initialTitle } : {},
    }));
    if (!validId || !loadTitle) return () => { active = false; };
    api.storyLoad({ id: storyId }, { signal: controller.signal }).then(response => {
      if (!response.success || Number(response.id) !== storyId) throw new Error('Story unavailable');
      if (active) setTitle(previous => ({ key: titleKey, status: 'ready', story: {
        id: response.id,
        title: typeof response.title === 'string' && response.title.trim() ? response.title : previous.story.title || '',
      } }));
    }).catch(() => {
      if (active) setTitle(previous => ({ ...previous, status: 'error' }));
    });
    return () => { active = false; controller.abort(); };
  }, [storyId, validId, attempt, loadTitle, initialTitle, titleKey]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const empty = { key, list: [], emptyReason: null };
    setAccepted(previous => previous?.scopeKey === scopeKey ? previous : null);
    setData({ ...empty, status: validId ? 'loading' : 'error' });
    if (!validId || composing) return () => { active = false; };
    const load = async () => {
      try {
        const filter = area === undefined ? {} : { area };
        const response = kind === 'statistics'
          ? await api.storyStatistic({ id: storyId, ...filter }, { signal: controller.signal })
          : await api.storyUsers({ story_id: storyId, ...filter, ...(text ? { q: text } : {}) }, { signal: controller.signal });
        if (!active || viewer !== feedViewerContext()) return;
        if (response.statusCode === 401) {
          setAccepted(null);
          setData({ ...empty, status: 'unauthorized' });
          return;
        }
        if (!response.success || !Array.isArray(response.list)) throw new Error('Content unavailable');
        const result = { ...empty, status: 'ready', list: response.list,
          emptyReason: ['no_following', 'country_unset'].includes(response.emptyReason) ? response.emptyReason : null };
        setData(result);
        setAccepted({ ...result, scopeKey });
      } catch {
        if (active && viewer === feedViewerContext()) setData({ ...empty, status: 'error' });
      }
    };
    // Search is debounced; switching groups/clearing search is immediate.
    // Late responses are never shown under a newer filter.
    const timer = text ? setTimeout(load, 250) : null;
    if (!text) load();
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [storyId, kind, validId, attempt, area, text, composing, viewer, scopeKey, key]);

  const visibleTitle = title.key === titleKey ? title : { status: 'loading', story: knownStory };
  const visibleData = data.key === key ? data : { status: 'loading', list: [], emptyReason: null };
  const status = !validId || visibleTitle.status === 'error' || visibleData.status === 'error' ? 'error'
    : visibleData.status === 'unauthorized' ? 'unauthorized'
      : visibleTitle.status === 'ready' && visibleData.status === 'ready' ? 'ready' : 'loading';
  // Both event sections use the same audience notices. Pending/failed title
  // requests must retain their loading/error state instead of showing one early.
  const noticeKey = status === 'unauthorized' ? (area === 'country' ? 'storyLocalSignIn' : 'storyFriendsSignIn')
    : status !== 'ready' ? null
      : visibleData.emptyReason === 'no_following' ? 'storyNoFollowing'
        : visibleData.emptyReason === 'country_unset' ? 'storyCountryUnset' : null;
  // Retain only successful content belonging to this event/account/language.
  // Consumers mark it inert while the selected audience/search is pending;
  // failed/unauthorized requests always replace it with the relevant notice.
  const retained = status === 'loading' && accepted?.scopeKey === scopeKey
    && accepted.list.length > 0 && !accepted.emptyReason;
  return { ...visibleData, list: retained ? accepted.list : visibleData.list,
    story: visibleTitle.story, status, retained, noticeKey, retry };
}
